import {
  DestinationEntityRecordStatus,
  FailedDestinationEntityRecord,
  ICustomMappingHandler,
  IDestinationEntity,
  IDestinationEntityJobRunOptions,
  IDestinationEntityRecord,
  IGetRecordResponse,
  IMigratedDataSummary,
  IPracticeMigration,
  ISourceEntityHandler,
  ISourceEntityRecord,
  ISourceEntityTypes,
  ITranslationMap,
  MergeConflictDestinationEntityRecord,
  SkippedDestinationEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  Brand,
  Organisation,
  Staffer,
  hasMergeConflicts,
} from '@principle-theorem/principle-core';
import {
  IBrand,
  IOrganisation,
  IProviderData,
  IStaffer,
  IUser,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  FirestoreMigrate,
  INamedDocument,
  PrimaryKey,
  WithPrimaryKey,
  WithRef,
  asDocRef,
  snapshotCombineLatest,
  toTimestamp,
} from '@principle-theorem/shared';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PracticeMigration } from '../../practice-migrations';
import { TranslationMapHandler } from '../../translation-map';
import { BaseDestinationEntity } from '../base-destination-entity';

export const USER_CUSTOM_MAPPING_TYPE = 'user';

export const STAFFER_RESOURCE_TYPE = 'staffer';

export interface IStafferJobData<T extends ISourceEntityTypes> {
  stafferRecord: IGetRecordResponse<T>;
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[];
  organisation: WithRef<IOrganisation>;
  brand: WithRef<IBrand>;
}

export interface IStafferMigrationData {
  sourceStafferId: string;
  user: IUser;
  stafferDetails: Pick<IProviderData, 'providerNumber' | 'providerModality'>;
}

export interface IStafferDestinationRecord {
  sourceRef: PrimaryKey<ISourceEntityRecord>;
  userRef: DocumentReference<IUser>;
  stafferRef: DocumentReference<IStaffer>;
}

export abstract class BaseStafferDestinationEntity<
  StafferRecord extends ISourceEntityTypes,
> extends BaseDestinationEntity<
  IStafferDestinationRecord,
  IStafferJobData<StafferRecord>,
  IStafferMigrationData
> {
  abstract stafferSourceEntity: ISourceEntityHandler<StafferRecord>;
  abstract stafferCustomMapping: ICustomMappingHandler<IStaffer>;
  abstract staffToUserCustomMapping?: ICustomMappingHandler<IUser>;

  get sourceCountComparison(): ISourceEntityHandler<StafferRecord> {
    return this.stafferSourceEntity;
  }

  sourceCountDataAccessor(
    data: IStafferJobData<StafferRecord>
  ): PrimaryKey<ISourceEntityRecord> {
    return data.stafferRecord.record.id;
  }

  getDestinationEntityRecordUid(data: IStafferJobData<StafferRecord>): string {
    return data.stafferRecord.record.uid;
  }

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMapHandler: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IStafferJobData<StafferRecord>[]> {
    const staff$ = this.stafferCustomMapping.getRecords(translationMapHandler);
    const organisation$ = PracticeMigration.organisation$(migration);
    const brand$ = PracticeMigration.brand$(migration);

    return snapshotCombineLatest([organisation$, brand$, staff$]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.stafferSourceEntity,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(([staffRecords, [organisation, brand, staff]]) =>
        staffRecords.map((stafferRecord) => ({
          stafferRecord,
          organisation,
          brand,
          staff,
        }))
      )
    );
  }

  getMigratedData$(
    record: IDestinationEntityRecord<IStafferDestinationRecord>
  ): Observable<IMigratedDataSummary[]> {
    if (record.status !== DestinationEntityRecordStatus.Migrated) {
      return of([]);
    }

    return combineLatest([
      Firestore.doc$(record.data.userRef),
      Firestore.doc$(record.data.stafferRef),
    ]).pipe(
      map(([user, staffer]) => [
        {
          label: 'User',
          data: user,
        },
        {
          label: 'Staffer',
          data: staffer,
        },
      ])
    );
  }

  async hasMergeConflict(
    translationMap: TranslationMapHandler,
    data: IStafferMigrationData
  ): Promise<IStafferMigrationData | undefined> {
    const existingUserRef = await translationMap.getDestination(
      data.sourceStafferId,
      USER_CUSTOM_MAPPING_TYPE
    );

    if (!existingUserRef) {
      return;
    }

    const existingStafferRef = await translationMap.getDestination(
      data.sourceStafferId,
      this.stafferSourceEntity.sourceEntity.metadata.idPrefix
    );

    if (!existingStafferRef) {
      return;
    }

    try {
      const existingUser = await Firestore.getDoc(
        asDocRef<IUser>(existingUserRef)
      );
      const existingStaffer = await Firestore.getDoc(
        asDocRef<IStaffer>(existingStafferRef)
      );

      const hasMergeConflict =
        hasMergeConflicts(data.user, existingUser) ||
        hasMergeConflicts(data.stafferDetails, existingStaffer);

      if (hasMergeConflict) {
        return {
          ...data,
          user: existingUser,
        };
      }
    } catch {
      return;
    }
  }

  buildMergeConflictRecord(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    jobData: IStafferJobData<StafferRecord>,
    _migrationData: IStafferMigrationData
  ): IDestinationEntityRecord & MergeConflictDestinationEntityRecord {
    return {
      uid: jobData.stafferRecord.record.uid,
      label: jobData.stafferRecord.record.label,
      status: DestinationEntityRecordStatus.MergeConflict,
      sourceRef: jobData.stafferRecord.record.id,
    };
  }

  async runJob(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMapHandler: TranslationMapHandler,
    jobData: IStafferJobData<StafferRecord>,
    migrationData: IStafferMigrationData
  ): Promise<IDestinationEntityRecord> {
    const stafferCustomMapOverride = jobData.staff.find(
      (staffer) =>
        staffer.sourceIdentifier ===
        this.stafferSourceEntity
          .getSourceRecordId(jobData.stafferRecord.data)
          .toString()
    );

    if (stafferCustomMapOverride?.destinationIdentifier) {
      const stafferRef = stafferCustomMapOverride?.destinationIdentifier;
      await translationMapHandler.upsert({
        sourceIdentifier: migrationData.sourceStafferId,
        destinationIdentifier: stafferRef,
        resourceType: this.stafferSourceEntity.sourceEntity.metadata.idPrefix,
      });
      return this.buildSkippedResponse(jobData);
    }

    const staffToUserMapOverride =
      await this.staffToUserCustomMapping?.getBySource(
        this.stafferSourceEntity
          .getSourceRecordId(jobData.stafferRecord.data)
          .toString(),
        translationMapHandler
      );

    let userRef: DocumentReference<IUser> | undefined =
      staffToUserMapOverride?.destinationIdentifier;

    if (userRef) {
      await translationMapHandler.upsert({
        sourceIdentifier: this.stafferSourceEntity
          .getSourceRecordId(jobData.stafferRecord.data)
          .toString(),
        destinationIdentifier: userRef,
        resourceType: USER_CUSTOM_MAPPING_TYPE,
      });
    }

    if (!userRef) {
      userRef = await this._upsertUser(
        translationMapHandler,
        jobData.organisation,
        migrationData
      );
    }

    if (!userRef) {
      throw new Error('Failed to create user');
    }

    const stafferRef = await this._upsertStaffer(
      jobData.brand,
      userRef,
      translationMapHandler,
      migrationData,
      migration
    );

    return this._buildSuccessResponse(
      {
        name: migrationData.user.name,
        ref: stafferRef,
      },
      jobData.stafferRecord,
      userRef
    );
  }

  buildSkippedResponse(
    data: IStafferJobData<StafferRecord>
  ): IDestinationEntityRecord & SkippedDestinationEntityRecord {
    return {
      uid: data.stafferRecord.record.uid,
      label: data.stafferRecord.record.label,
      status: DestinationEntityRecordStatus.Skipped,
      sourceRef: data.stafferRecord.record.id,
    };
  }

  protected _buildErrorResponse(
    staffer: Pick<
      IGetRecordResponse<StafferRecord>['record'],
      'label' | 'uid' | 'id'
    >,
    errorMessage?: string
  ): IDestinationEntityRecord & FailedDestinationEntityRecord {
    return {
      uid: staffer.uid,
      label: staffer.label,
      status: DestinationEntityRecordStatus.Failed,
      sourceRef: staffer.id,
      errorMessage: errorMessage ?? `Can't resolve staffer data`,
      failData: {
        stafferRef: staffer.id,
      },
    };
  }

  protected _buildSuccessResponse(
    staffer: INamedDocument<IStaffer>,
    record: IGetRecordResponse<StafferRecord>,
    userRef: DocumentReference<IUser>
  ): IDestinationEntityRecord<IStafferDestinationRecord> {
    return {
      uid: record.record.uid,
      label: staffer.name,
      data: {
        sourceRef: record.record.id,
        userRef,
        stafferRef: staffer.ref,
      },
      status: DestinationEntityRecordStatus.Migrated,
      sourceRef: record.record.id,
      migratedAt: toTimestamp(),
    };
  }

  private async _upsertStaffer(
    brand: WithRef<IBrand>,
    userRef: DocumentReference<IUser>,
    translationMap: TranslationMapHandler,
    migrationData: IStafferMigrationData,
    migration: WithRef<IPracticeMigration>
  ): Promise<DocumentReference<IStaffer>> {
    const existingStafferRef = await translationMap.getDestination(
      migrationData.sourceStafferId,
      this.stafferSourceEntity.sourceEntity.metadata.idPrefix
    );

    const stafferRef = await FirestoreMigrate.upsertDoc(
      Brand.stafferCol(brand),
      Staffer.init({
        user: {
          name: migrationData.user.name,
          ref: userRef,
        },
        providerDetails: migration.configuration.practices.map((practice) => ({
          practiceRef: practice.ref,
          providerNumber: migrationData.stafferDetails.providerNumber,
          providerModality: migrationData.stafferDetails.providerModality,
        })),
      }),
      existingStafferRef?.id
    );

    await this.stafferCustomMapping?.upsertRecord(
      {
        sourceIdentifier: migrationData.sourceStafferId,
        destinationIdentifier: stafferRef,
      },
      translationMap
    );

    if (!existingStafferRef) {
      await translationMap.upsert({
        sourceIdentifier: migrationData.sourceStafferId,
        sourceLabel: migrationData.user.name,
        destinationIdentifier: stafferRef,
        resourceType: this.stafferSourceEntity.sourceEntity.metadata.idPrefix,
      });
    }
    return stafferRef;
  }

  private async _upsertUser(
    translationMap: TranslationMapHandler,
    organisation: WithRef<IOrganisation>,
    migrationData: IStafferMigrationData
  ): Promise<DocumentReference<IUser> | undefined> {
    const userDestinationRef = await translationMap.getDestination(
      migrationData.sourceStafferId,
      USER_CUSTOM_MAPPING_TYPE
    );
    const userRef = await FirestoreMigrate.upsertDoc(
      Organisation.userCol(organisation),
      migrationData.user,
      userDestinationRef?.id
    );

    await translationMap.upsert({
      sourceIdentifier: migrationData.sourceStafferId,
      destinationIdentifier: userRef,
      resourceType: USER_CUSTOM_MAPPING_TYPE,
    });

    return userRef;
  }
}
